import React, { useEffect, useRef, useState } from 'react';
import {
  runSpeedTest,
  type SpeedTestPhase,
  type SpeedTestResult,
} from '../../lib/speedTest';

interface SpeedTestModalProps {
  onClose: () => void;
}

type ModalState =
  | { status: 'idle' }
  | { status: 'running'; phase: SpeedTestPhase }
  | { status: 'done'; result: SpeedTestResult }
  | { status: 'error'; message: string }
  | { status: 'offline' };

function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

const SpeedTestModal: React.FC<SpeedTestModalProps> = ({ onClose }) => {
  const [state, setState] = useState<ModalState>(() =>
    isOnline() ? { status: 'idle' } : { status: 'offline' },
  );
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);

  const runTest = () => {
    if (!isOnline()) {
      setState({ status: 'offline' });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const runId = ++runIdRef.current;

    setState({ status: 'running', phase: 'download' });

    runSpeedTest({
      signal: controller.signal,
      onPhase: (phase) => {
        if (runId !== runIdRef.current) return;
        setState({ status: 'running', phase });
      },
    })
      .then((result) => {
        if (runId !== runIdRef.current) return;
        setState({ status: 'done', result });
      })
      .catch((err: unknown) => {
        if (runId !== runIdRef.current) return;
        if (isAbortError(err)) return;
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'Speed test failed. Check the connection and try again.';
        setState({ status: 'error', message });
      });
  };

  // Auto-start once when the modal mounts.
  useEffect(() => {
    runTest();
    return () => {
      runIdRef.current += 1;
      abortRef.current?.abort();
      abortRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only auto-start
  }, []);

  const handleClose = () => {
    runIdRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    onClose();
  };

  const running = state.status === 'running';
  const canRerun = state.status !== 'running' && state.status !== 'offline';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="speed-test-title"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 id="speed-test-title" className="text-base font-semibold text-gray-900">
            Internet speed test
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          {state.status === 'offline' && (
            <p className="text-sm text-red-600 font-medium">
              No internet connection
            </p>
          )}

          {state.status === 'running' && (
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <span
                className="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
                aria-hidden
              />
              <span>
                {state.phase === 'download'
                  ? 'Testing download…'
                  : 'Testing upload…'}
              </span>
            </div>
          )}

          {state.status === 'done' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
                  Download
                </div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {state.result.downloadMbps.toFixed(1)}
                </div>
                <div className="text-xs text-gray-500">Mbps</div>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-3 text-center">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
                  Upload
                </div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {state.result.uploadMbps.toFixed(1)}
                </div>
                <div className="text-xs text-gray-500">Mbps</div>
              </div>
            </div>
          )}

          {state.status === 'error' && (
            <p className="text-sm text-red-600">{state.message}</p>
          )}

          <p className="text-[11px] text-gray-400 leading-snug">
            Uses Google/M-Lab NDT (same engine as Google’s speed test). Results
            may be contributed to M-Lab’s open dataset. Not a Firebase latency
            test.
          </p>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 flex gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={runTest}
            disabled={!canRerun || running}
            className="flex-1 py-2 px-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state.status === 'done' || state.status === 'error'
              ? 'Run again'
              : 'Run test'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpeedTestModal;
